import React, { useEffect, useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "@/utils/appToast";
import { api } from "@/utils/api";
import { loginUser } from "@/features/user/userSlice";
import { SetStorage } from "@/shared/services/credentialsService";
import { createMetaConversionPayload, fireMetaPixelEvent } from "@/utils/metaPixel";

interface GoogleSignUpProps {
  type: "worker" | "client";
  onSuccess?: (userData: any) => void;
  /**
   * Controls whether this component shows its own generic
   * "Signup successful!" toast on backend success.
   * For flows that want to fully control messaging in the parent,
   * pass false.
   */
  showSuccessToast?: boolean;
}

const GoogleSignUp: React.FC<GoogleSignUpProps> = ({
  type,
  onSuccess,
  showSuccessToast = true,
}) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const handleCredentialResponse = useCallback(
    async (response: { credential: string }) => {
      try {
        const token = response.credential;
        if (!token) throw new Error("Google token not received");

        setIsLoading(true);

        const meta = createMetaConversionPayload();

        // Call backend API
        const { status, data } = await api.googleSignUp({
          token,
          type: type === "client" ? "user" : "worker",
          ...meta,
        });

        if (status === "success") {
          if (showSuccessToast) {
            toast.success("Signup successful!");
          }

          fireMetaPixelEvent("CompleteRegistration", meta.eventId);

          // Store token
          localStorage.setItem("token", data.token);

          // Store user data and login
          const { token: userToken, user } = data;
          dispatch(loginUser({ token: userToken, user }));

          // Store in storage service
          const storageData = {
            data: {
              token: userToken,
              user: user,
            },
          };
          SetStorage(storageData, true);

          // Call onSuccess callback - let the parent component handle redirect logic
          if (onSuccess) {
            onSuccess(user);
          }
        } else {
          toast.error(data.message || "Signup failed");
        }
      } catch (err: any) {
        console.error("Google signup error:", err);
        toast.error(err.message || "Google signup failed");
      } finally {
        setIsLoading(false);
      }
    },
    [type, dispatch, onSuccess, showSuccessToast]
  );

  useEffect(() => {
    const initializeGoogle = () => {
      if (
        (window as any).google &&
        document.getElementById("google-signup-button")
      ) {
        // Initialize Google Identity
        (window as any).google.accounts.id.initialize({
          client_id: (import.meta as any).env.VITE_APP_GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
        });

        // Render the Google Sign-Up button
        (window as any).google.accounts.id.renderButton(
          document.getElementById("google-signup-button")!,
          { theme: "outline", size: "large", width: "100%" }
        );

        // Optional: prompt automatically
        // (window as any).google.accounts.id.prompt();
      } else {
        console.warn("Google script not loaded yet.");
      }
    };

    // Small delay to make sure script is loaded
    const timer = setTimeout(initializeGoogle, 500);

    return () => clearTimeout(timer);
  }, [handleCredentialResponse]);

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <div
        id="google-signup-button"
        style={{
          width: "100%",
          opacity: isLoading ? 0.6 : 1,
          pointerEvents: isLoading ? "none" : "auto",
        }}
      />
      {isLoading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div
            className="spinner-border"
            style={{ width: "20px", height: "20px" }}
            role="status"
          />
        </div>
      )}
    </div>
  );
};

export default GoogleSignUp;
